<template>
  <span>
    <b-button class="mb-1 custom-wizard-btn" @click="openExportModal">
      <b-icon icon="download" class="mr-2"></b-icon> Generate ID Card
    </b-button>

    <b-modal id="id-card-export-modal" title="Generate ID Card" size="md" @hidden="exportLoading = false">
      <div v-if="exportLoading" class="text-center py-3">
        <b-spinner variant="primary"></b-spinner>
        <p class="mt-2 text-muted">Generating ID cards, please wait…</p>
      </div>

      <b-form v-else>
        <b-row>
          <b-col cols="6">
            <b-form-group label="Issued Date" label-for="issued_date" class="required-field">
              <b-input-group>
                <b-form-input v-model="exportForm.issued_date" placeholder="YYYY-MM-DD" id="issued_date" />
                <b-input-group-append>
                  <b-form-datepicker v-model="exportForm.issued_date" button-only right locale="en-US" />
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group label="Expiry Date" label-for="expiry_date" class="required-field">
              <b-input-group>
                <b-form-input v-model="exportForm.expiry_date" placeholder="YYYY-MM-DD" id="expiry_date" />
                <b-input-group-append>
                  <b-form-datepicker v-model="exportForm.expiry_date" button-only right locale="en-US" />
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-row>
        <b-alert show variant="info" class="mb-0 small">
          <i class="fa fa-info-circle mr-1"></i>
          {{ employee_ids.length }} employee(s) selected. Cards will be generated using the active ID Card Template.
        </b-alert>
      </b-form>

      <template #modal-footer="{ cancel }">
        <b-button variant="secondary" @click="cancel()" :disabled="exportLoading">Cancel</b-button>
        <b-button variant="primary" @click="generateAndDownload" :disabled="exportLoading">
          <i class="fa fa-download mr-1"></i> Generate &amp; Download ZIP
        </b-button>
      </template>
    </b-modal>
  </span>
</template>

<script>
export default {
  props: ['employee_ids'],
  data() {
    return {
      exportLoading: false,
      exportForm: {
        issued_date: new Date().toISOString().slice(0, 10),
        expiry_date: new Date(new Date().setFullYear(new Date().getFullYear() + 2)).toISOString().slice(0, 10),
      },
    }
  },
  methods: {
    isValidDate(value) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
      const date = new Date(value);
      return !isNaN(date.getTime());
    },
    openExportModal() {
      if (!this.employee_ids || this.employee_ids.length === 0) {
        this.$bvToast.toast('Please select at least one employee.', {
          title: 'No Employee Selected',
          variant: 'danger',
          solid: true,
        });
        return;
      }
      this.$bvModal.show('id-card-export-modal');
    },
    generateAndDownload() {
      if (this.exportLoading) return;
      
      const issued = (this.exportForm.issued_date || '').toString().trim();
      const expiry = (this.exportForm.expiry_date || '').toString().trim();

      if (!issued || !expiry) {
        this.$bvToast.toast('Issued and Expiry dates are required.', {
          title: 'Validation Error',
          variant: 'danger',
          solid: true,
        });
        return;
      }

      if (!this.isValidDate(issued) || !this.isValidDate(expiry)) {
        this.$bvToast.toast('Please enter valid dates in YYYY-MM-DD format.', {
          title: 'Validation Error',
          variant: 'danger',
          solid: true,
        });
        return;
      }

      if (new Date(expiry) < new Date(issued)) {
        this.$bvToast.toast('Expiry date cannot be earlier than the issued date.', {
          title: 'Validation Error',
          variant: 'danger',
          solid: true,
        });
        return;
      }

      this.exportLoading = true;

      axios.post('/id-card/generate-zip', {
        employee_ids: this.employee_ids,
        issued_date: issued,
        expiry_date: expiry
      }, { responseType: 'blob' })
      .then(response => {
        // Plain JS download logic (no extra libraries)
        const blob = new Blob([response.data], { type: 'application/zip' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'ID_Cards_' + new Date().getTime() + '.zip');
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);

        this.exportLoading = false;
        this.$bvModal.hide('id-card-export-modal');
        this.$bvToast.toast('ZIP file downloaded successfully.', {
          title: 'Success',
          variant: 'success',
          solid: true,
        });
      })
      .catch(error => {
        console.error("Export error:", error);
        this.exportLoading = false;

        // The request asks for a blob, so the server's JSON error body — the
        // 403 for a selection containing another company's employee, the 422
        // batch cap — comes back as a Blob and has to be read before it can be
        // shown. Without this every failure looked like a template problem.
        const showError = message => {
          this.$bvToast.toast(message || 'Failed to generate ZIP file. Please check template settings.', {
            title: 'Error',
            variant: 'danger',
            solid: true,
          });
        };

        const data = error.response && error.response.data;

        if (data && typeof data.text === 'function') {
          data.text()
            .then(text => {
              let parsed = {};
              try {
                parsed = JSON.parse(text) || {};
              } catch (e) {
                parsed = {};
              }
              showError(parsed.error || parsed.message);
            })
            .catch(() => showError(null));
          return;
        }

        showError(data && (data.error || data.message));
      });
    }
  }
}
</script>
